import { ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  model,
  signal,
  AfterViewInit,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { SearchType } from '../../../enums/search-type';
import { AnswerSearch } from '../../../models/recent-search';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { SearchPanelComponent } from '../search-panel/search-panel.component';
import { OpenSearchService } from '../../../services/open-search.service';
import { ChangeLanguagePipe } from '../../../pipes/change-language.pipe';
import { TranslatableField } from '../../../models/TranslatableField';
import { DrawerSearchPanelService } from '../../../services/drawer-search-panel.service';
import { OpenSearchDrawerService } from '../../../services/open-search-drawer.service';
import { OpenSearchComponent } from '../open-search.component';

@Component({
  selector: 'app-textarea-search',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatIconModule,
    MatTooltipModule,
    SearchPanelComponent,
    MatDividerModule,
    MatMenuModule,
  ],
  templateUrl: './textarea-search.component.html',
  styleUrl: './textarea-search.component.scss',
})
export class TextareaSearchComponent implements AfterViewInit {
  @ViewChild('autoGrow') autoGrowRef!: ElementRef<HTMLTextAreaElement>;

  textControl = new FormControl('');

  private cdr = inject(ChangeDetectorRef);
  isShowAllResults = model<boolean>(false);
  showSearchPanel = model<boolean>();
  isComponentResponseView = model<boolean>();
  showAllResults = model<boolean>(false);
  showRecentSearchPanel = model<boolean>(false);
  showQuickSearchPanel = model<boolean>(false);
  inputText = model<TranslatableField | null>(null);
  queryMode = model<'default' | 'curated-lessons'>('default');
  searchType = model<SearchType>();
  items = model<AnswerSearch[]>([]);
  showResponseView = model<boolean>(false);
  activeDestroyComponent = model<boolean>(false);
  private openSearchService = inject(OpenSearchService);
  private changeLanguagePipe = inject(ChangeLanguagePipe);
  private drawerSearchPanelService = inject(DrawerSearchPanelService);
  private openSearchDrawerService = inject(OpenSearchDrawerService);
  lastPromptText = signal<string>('');
  historyResponse = computed(() => this.openSearchService.historyResponse());
  spinnerConversational = computed(() => this.openSearchService.spinnerConversational());
  loadingConversational = computed(() => this.openSearchService.spinnerConversational());
  isDisabledComponent = computed(
    () =>
      this.openSearchService.spinnerConversational() &&
      this.isComponentResponseView() &&
      this.historyResponse() !== null,
  );
  showOnlyNewSearch = model<boolean>();
  openSearch = inject(OpenSearchComponent);

  constructor() {
    effect(() => {
      if (this.spinnerConversational()) {
        this.textControl.disable({ emitEvent: false });
      } else {
        this.textControl.enable({ emitEvent: false });
      }
    });

    effect(() => {
      const promptText = this.drawerSearchPanelService.selectedPromptText();
      if (!promptText) return;
      if (this.spinnerConversational()) {
        this.drawerSearchPanelService.selectedPromptText.set(null);
        return;
      }
      this.setTextareaValue(promptText);
      this.drawerSearchPanelService.selectedPromptText.set(null);
      setTimeout(() => {
        this.autoGrowRef?.nativeElement?.focus();
        this.cdr.detectChanges();
      });
    });
  }

  ngAfterViewInit(): void {
    this.adjustHeight();
  }

  private adjustHeight(): void {
    setTimeout(() => {
      const el = this.autoGrowRef?.nativeElement;
      if (el) {
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
      }
    });
  }

  private setTextareaValue(text: string): void {
    this.textControl.setValue(text, { emitEvent: false });
    this.adjustHeight();
  }

  onInput(): void {
    this.adjustHeight();
  }

  onEnterKey(event: Event): void {
    if ((event as KeyboardEvent).shiftKey) {
      return;
    }
    event.preventDefault();
    this.onSearch();
  }

  onSearch(): void {
    const rawText = this.textControl.value || '';
    const searchText = rawText.trim();
    if (!searchText || this.spinnerConversational()) return;
    this.drawerSearchPanelService.lastSentText.set(searchText);
    this.lastPromptText.set(searchText);
    this.drawerSearchPanelService.selectedPromptText.set(null);
    localStorage.removeItem('OpenSearchPath');
    const translatable = this.openSearchService.convertTextToTranslateItem(searchText);
    this.inputText.set(translatable);
    this.setTextIntoLocalStorage();
    this.setTextareaValue('');
    this.inputText.set(null);
    this.openSearchService.inputText.set(searchText);
    const modeType = this.queryMode() === 'curated-lessons' ? 'lessons' : undefined;
    this.openSearchService.postDrawer(searchText, modeType);
    this.showAllResults.set(true);
    this.activeDestroyComponent.set(true);
    this.showResponseView.set(true);
    const scrollContainer = document.querySelector('.response-content');
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: 'smooth' });
    }
    this.cdr.detectChanges();
    this.openSearchService._dataOpenLogic.set({
      showAllResults: this.showAllResults() || false,
      activeDestroyComponent: this.activeDestroyComponent(),
      showResponseView: this.showResponseView(),
    });
  }

  setTextIntoLocalStorage(): void {
    if (!this.inputText()) return;

    const newSearch: AnswerSearch = {
      text: this.inputText() || {},
      date: new Date(),
      selected: true,
    };

    if (!newSearch.date || !this.changeLanguagePipe.transform(newSearch.text)) return;

    let itemsForUpdate: AnswerSearch[] = [];
    if (this.searchType() !== SearchType.RECENT) {
      itemsForUpdate = [...JSON.parse(localStorage.getItem('recentSearchItems') || '[]')];
    } else {
      itemsForUpdate = [...this.items()];
    }

    const isSameDay = (d1: Date, d2: Date) =>
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate();

    itemsForUpdate = itemsForUpdate.filter(
      item =>
        !(
          this.changeLanguagePipe.transform(item.text).toLowerCase() ===
            this.changeLanguagePipe.transform(newSearch.text).toLowerCase() &&
          item.date &&
          isSameDay(new Date(item.date), newSearch.date ?? new Date())
        ),
    );

    itemsForUpdate.push(newSearch);
    this.items.set(
      itemsForUpdate.sort(
        (a, b) => new Date(b.date ?? '').getTime() - new Date(a.date ?? '').getTime(),
      ),
    );
    this.addRecentSearchInLocalstorage();
  }

  addRecentSearchInLocalstorage(): void {
    const data = this.items();
    if (data.length > 0) {
      localStorage.setItem('recentSearchItems', JSON.stringify(data));
    }
  }

  openQuickSearch(): void {
    this.openSearchService.openedPanelFromResponse.set(true);
    this.openSearchDrawerService.close();
    setTimeout(() => {
      this.openSearchService.getPromts(0, true);
      this.searchType.set(SearchType.QUICK);
      this.drawerSearchPanelService.open({
        open: true,
        searchType: SearchType.QUICK,
        showSearchPanel: true,
        showAll: false,
        showAllResults: false,
        isShowAllResults: false,
      });
    }, 200);
  }

  openRecentSearch(): void {
    this.openSearchService.openedPanelFromResponse.set(true);
    this.openSearchDrawerService.close();
    setTimeout(() => {
      this.openSearchService.getRecentSeaeches();
      this.searchType.set(SearchType.RECENT);
      this.drawerSearchPanelService.open({
        open: true,
        searchType: SearchType.RECENT,
        showAll: false,
        showAllResults: false,
        isShowAllResults: false,
      });
    }, 200);
  }

  onNewSearchClick(): void {
    localStorage.setItem('OpenSearchPath', 'true');
    this.openSearchDrawerService.close();
    this.drawerSearchPanelService.close();
    this.showOnlyNewSearch.set(true);
    this.setTextareaValue('');
    this.inputText.set(null);
    this.openSearchService.historyResponse.set(null);
    this.openSearchService.conversationIdSignal.set(null);
    this.showSearchPanel.set(false);
    this.showAllResults.set(false);
  }

  onCancelSearch(): void {
    const textToRestore =
      this.drawerSearchPanelService.lastSentText() || this.textControl.value || '';
    this.openSearchService.cancelRequest();
    this.openSearchService.spinnerConversational.set(false);
    this.drawerSearchPanelService.selectedPromptText.set(null);
    const history = this.openSearchService.historyResponse();
    const interactions = history?.openQueryInteractions ?? [];
    const answeredInteractions = interactions.filter(
      item => item.response !== null && item.response !== undefined,
    );
    if (answeredInteractions.length === 0) {
      localStorage.setItem('OpenSearchPath', 'true');
      this.openSearchDrawerService.close();
      this.drawerSearchPanelService.close();
      this.showOnlyNewSearch.set(true);
      this.inputText.set(null);
      this.openSearchService.historyResponse.set(null);
      this.openSearchService.conversationIdSignal.set(null);
      this.showSearchPanel.set(false);
      this.showAllResults.set(false);
    } else {
      const lastItem = interactions[interactions.length - 1];
      if (lastItem && lastItem.response === null) {
        this.openSearchService.historyResponse.set({
          ...history!,
          openQueryInteractions: answeredInteractions,
        });
      }
    }
    setTimeout(() => {
      this.setTextareaValue(textToRestore);
      this.inputText.set(this.openSearchService.convertTextToTranslateItem(textToRestore));
      this.autoGrowRef?.nativeElement?.focus();
      this.lastPromptText.set('');
      this.drawerSearchPanelService.lastSentText.set('');
      this.cdr.detectChanges();
    }, 50);
  }
}
